import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useInboxGrouping } from '@/hooks/inbox/useInboxGrouping';
import type { InboxPage } from '@/services/conversations/inbox';
import {
  getQuestionGroupChatTabs,
  type QuestionGroup,
  type QuestionGroupChatTab,
} from '@/util/questionGroups';

/**
 * The inquiry group holding an expert thread, from the inbox pages already loaded. A grouped list's
 * key ends in its grouping, so only the lists the cards came from are read.
 */
export function findCachedQuestionGroup(
  queryClient: QueryClient,
  conversationId: string
): QuestionGroup | undefined {
  const lists = queryClient.getQueriesData<{ pages?: (InboxPage | undefined)[] }>({
    queryKey: ['inquiriesV2'],
    predicate: (query) => query.queryKey[4] === 'question',
  });

  for (const [, data] of lists) {
    for (const page of data?.pages ?? []) {
      const group = page?.questionGroups?.find((candidate) =>
        candidate.threads.some((thread) => thread.conversationId === conversationId)
      );

      if (group) {
        return group;
      }
    }
  }

  return undefined;
}

/**
 * The chat tabs for a conversation opened from an inquiry group: one per expert thread on the card.
 *
 * Only where the inbox shows the cards, and only from the card the inbox already loaded, so the
 * tabs cannot list a thread the card does not. Undefined everywhere else, which leaves the chat's
 * own tabs exactly as they were: with the cards off, for submitters and experts, and for a
 * conversation reached without its card loaded, such as a link opened cold.
 */
export function useQuestionGroupChatTabs(
  conversationId: string | undefined
): QuestionGroupChatTab[] | undefined {
  const isQuestionGrouping = useInboxGrouping() === 'question';

  const queryClient = useQueryClient();

  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient]
  );

  const getGroup = useCallback(
    () =>
      isQuestionGrouping && conversationId
        ? findCachedQuestionGroup(queryClient, conversationId)
        : undefined,
    [conversationId, isQuestionGrouping, queryClient]
  );

  const questionGroup = useSyncExternalStore(subscribe, getGroup, getGroup);

  return useMemo(
    () =>
      questionGroup && conversationId
        ? getQuestionGroupChatTabs(questionGroup, conversationId)
        : undefined,
    [conversationId, questionGroup]
  );
}
